/**
 * Client API helper. Calls the server through the Vite `/api` proxy, parses the locked
 * `{data,meta,traceId}` envelope, and throws a typed `ProblemDetails` on problem+json errors
 * (so pages surface `title`/`detail`, never a raw stack). PHI is never logged here.
 *
 * Auth header is selected by `VITE_AUTH_MODE` (B2C client phase 2):
 *   - `basic` (DEFAULT): the V1 dev credential from `VITE_BASIC_AUTH` ("user:pass"), on-machine only.
 *   - `b2c`: `Authorization: Bearer <token>` from the MSAL token service (silent-first).
 * The b2c branch dynamically imports the token service so MSAL is never loaded in basic mode. The
 * `X-Correlation-Id` header is attached in both modes. PHI is never logged here.
 */
import type { z } from "zod";
import type { Envelope, Meta, ProblemDetails } from "@shared/contracts";
import { logger } from "@skypointcloud/logger/react";
import { isB2cMode } from "../auth/authMode";

// Module-level PHI-safe telemetry sink for the shape gate. Mirrors ErrorBoundary/reportMsalError: the
// Error object is NEVER passed (2nd arg undefined) and only sanitized, non-PHI breadcrumbs are logged.
const log = logger.createComponentLogger("api");

// F8 (PHI): a BRANDED string that can only be produced by sanitizePathSegment. Because a plain string is
// NOT assignable to SanitizedSegment, an UNsanitized (potentially PHI-derived) value is unrepresentable in
// ApiShapeIssue.path by construction — the sanitizer is the sole producer, so a future regression cannot
// route a raw payload key/value into telemetry without going through the scrub first. The brand is a
// compile-time phantom only (erased at runtime; a SanitizedSegment IS a plain string when logged).
declare const sanitizedSegmentBrand: unique symbol;
export type SanitizedSegment = string & { readonly [sanitizedSegmentBrand]: true };

/** One sanitized zod issue: our own schema field-path + the zod error code. NEVER carries a value. */
export interface ApiShapeIssue {
  /**
   * Dotted schema path (our keys / array indices), e.g. "0.diagnosis_code". Never a payload value. Typed
   * as the branded SanitizedSegment so it can ONLY be built from sanitizePathSegment output (F8).
   */
  path: SanitizedSegment;
  /** The zod issue code (e.g. "invalid_type"). A category, never the offending value. */
  code: string;
}

/**
 * Thrown when a response body fails its optional zod SHAPE gate (render-shape drift caught at the API
 * boundary instead of deep in render). Carries NO response data — only a stable, reassuring, no-jargon
 * `title` for the recovery UI and the SANITIZED issue list (field-path + code) for a debuggable, PHI-safe
 * breadcrumb. The raw payload, zod messages, expected/received, and input are all excluded by construction.
 */
export class ApiShapeError extends Error {
  /** Stable, non-PHI, no-jargon copy surfaced to the user (routes to the existing PageError recovery UI). */
  readonly title: string = "The server returned data in an unexpected format. Please try again.";
  /** Sanitized issues ONLY (path + code) — never any response value. */
  readonly issues: ReadonlyArray<ApiShapeIssue>;
  constructor(issues: ApiShapeIssue[]) {
    super("ApiShapeError");
    this.name = "ApiShapeError";
    // F9: defensively COPY + FREEZE so the caller's array (aliased at the throw site) can't mutate the
    // constructed error's issue list after the fact. The list is already bounded (capped at the emit site).
    this.issues = Object.freeze([...issues]);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  if (isB2cMode()) {
    // Dynamic import: MSAL/token service is only pulled in when b2c mode is active.
    const { getAccessToken } = await import("../auth/authService");
    const token = await getAccessToken();
    return token ? { Authorization: "Bearer " + token } : {};
  }
  const cred = import.meta.env.VITE_BASIC_AUTH as string | undefined;
  return cred ? { Authorization: "Basic " + btoa(cred) } : {};
}

// The server adopts an inbound X-Correlation-Id only when it matches this bounded charset/length (see
// server/middleware/correlation.ts). Mirror it on the client so (L2) a minted id is asserted to satisfy
// the server regex before we send it — a silent format drift would break the cross-resource join — and
// (L1) a server-echoed id is validated before we adopt it (defends against a crafted/oversized header).
const CORRELATION_ID_REGEX = /^[A-Za-z0-9-]{8,64}$/;

// Correlation id for this request: reuse the active id (set from a prior server-echoed response) or
// mint a fresh one. Sent as X-Correlation-Id so UI telemetry (appins-skycare-ui) and backend telemetry
// (appins-skycare-backend) join on the same value. Opaque token only — never PHI.
function currentCorrelationId(): string {
  const active = logger.getCorrelationId();
  if (active && CORRELATION_ID_REGEX.test(active)) return active;
  const minted = logger.generateCorrelationId();
  // L2: guarantee the minted id satisfies the server's accept regex; fall back to a known-good shape if
  // the logger ever changes its id format so the join is never silently broken.
  if (CORRELATION_ID_REGEX.test(minted)) return minted;
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Segments in a URL path that LOOK id-like (a pure number) but are stable ROUTE LITERALS — kept intact
// so the telemetry template stays readable and aggregatable (e.g. the "360" in /participants/:id/360).
const ROUTE_WORD_ALLOWLIST = new Set(["360"]);
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when a URL path segment looks like a dynamic IDENTIFIER (participant key, user id, uuid) rather
 * than a fixed resource word. Used by templatePath so telemetry never carries a raw id (which can be a
 * member/participant key) and instead logs a stable ":id" route template that aggregates cleanly. The
 * heuristic is deliberately conservative — a known route literal (see allowlist) is never treated as an id.
 */
function isIdSegment(seg: string): boolean {
  if (seg.length === 0 || ROUTE_WORD_ALLOWLIST.has(seg)) return false;
  if (/^-?\d+$/.test(seg)) return true; // integer surrogate key (incl. NEGATIVE keys the member pages accept)
  if (UUID_REGEX.test(seg)) return true; // uuid
  if (seg.length >= 20) return true; // long opaque token
  if (seg.length >= 12 && /\d/.test(seg)) return true; // digit-bearing opaque id
  return false;
}

/**
 * Turn a request path into a PHI-safe, aggregatable route TEMPLATE for telemetry: strip the query string
 * (it can carry PII like actorEmail) and replace id-like path segments (a participant key, a user id, a
 * uuid) with ":id". So /api/v1/participants/42/360 -> /api/v1/participants/:id/360 — no dynamic id (which
 * can be a member identifier) ever reaches App Insights, and every call to a route rolls up to one series.
 */
function templatePath(path: string): string {
  return path
    .split("?")[0]
    .split("/")
    .map((seg) => (isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

/**
 * Sanitize ONE zod issue-path segment before it can reach telemetry. issue.path can include a payload's
 * OWN object KEY as its first element (e.g. for a z.record value failure), so a raw segment could be a
 * PHI-derived string (a member id / MRN). KEEP a segment only if it is a non-negative integer array index
 * OR a short, conservative schema-identifier token; replace anything else with "*". This is defense-in-
 * depth — schemas that could surface payload keys (member360Schema) ALSO emit a static path token — but
 * this chokepoint guarantees no payload-derived string is ever logged even if a new schema regresses.
 */
function sanitizePathSegment(seg: PropertyKey): SanitizedSegment {
  // Single-chokepoint cast: this function is the SOLE producer of a SanitizedSegment (F8). The scrub logic
  // is unchanged — a non-negative integer array index or a short schema-identifier token survives; anything
  // else (a PHI-derived key, a symbol, a hyphen/@-bearing token) collapses to "*".
  const safe = ((): string => {
    if (typeof seg === "number") return Number.isInteger(seg) && seg >= 0 ? String(seg) : "*";
    if (typeof seg !== "string") return "*"; // a symbol key can never be a schema-defined field name
    if (/^\d+$/.test(seg)) return seg; // array index
    if (seg.length <= 40 && /^[A-Za-z_][A-Za-z0-9_]*$/.test(seg)) return seg; // schema-identifier token
    return "*";
  })();
  return safe as SanitizedSegment;
}

/**
 * Join already-sanitized path segments into one dotted SanitizedSegment. The SanitizedSegment[] parameter
 * guarantees at compile time that EVERY element passed through sanitizePathSegment, so the joined path is
 * PHI-safe by construction — this is the only place a full ApiShapeIssue.path is minted (F8).
 */
function joinSanitizedPath(segments: SanitizedSegment[]): SanitizedSegment {
  return segments.join(".") as SanitizedSegment;
}

export class ApiProblem extends Error {
  /**
   * The HTTP status of the failed response, surfaced as a first-class field so callers can
   * distinguish AUTHORIZATION failures (403) from transport/server failures (5xx/network) and
   * react differently — e.g. gate the app to AccessDenied on 403 vs a retry screen on 5xx.
   * Taken from `res.status` directly (not the parsed body), so it is reliable even when the
   * server omits `status` from the problem+json document.
   */
  readonly status: number;
  /**
   * True ONLY when this 401 came from a request that was ALREADY retried once with a force-refreshed
   * b2c token and STILL returned 401 (a FRESH token rejected for audience/scope/clock/provisioning
   * reasons). This distinguishes a TERMINAL "we cannot verify your access" state (re-logging in would
   * just mint another rejected token and loop) from an ordinary first-401 expired token (which the
   * silent retry recovers, or which maps to the recoverable "sign in again" screen). Never PHI.
   */
  readonly authRejected: boolean;
  constructor(
    public readonly problem: ProblemDetails,
    status?: number,
    authRejected = false,
  ) {
    super(problem.title);
    this.name = "ApiProblem";
    this.status = status ?? problem.status ?? 0;
    this.authRejected = authRejected;
  }
}

export async function fetchEnvelope<T>(
  path: string,
  init?: RequestInit,
  // Optional response-SHAPE gate. When provided, env.data is validated (safeParse) after a successful
  // parse; a structural mismatch throws a typed ApiShapeError instead of crashing deep in render. When
  // omitted, behavior is byte-for-byte unchanged. Callers pass module-level constant schemas.
  schema?: z.ZodType<T>,
  // Internal: set true on the ONE self-heal retry after a b2c 401 so recovery never loops. Callers
  // never pass this.
  _retried = false,
): Promise<{ data: T; meta?: Meta }> {
  const correlationId = currentCorrelationId();
  const auth = await authHeader();
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      "X-Correlation-Id": correlationId,
      ...auth,
      ...(init?.headers ?? {}),
    },
  });
  // Adopt the server-echoed correlation id so subsequent UI logs share the backend's value (the two
  // App Insights resources then join on the same correlationId custom dimension). L1: validate
  // charset/length before adopting so a crafted/oversized echoed header can't poison the client id.
  const echoed = res.headers.get("X-Correlation-ID");
  if (echoed && CORRELATION_ID_REGEX.test(echoed)) logger.setCorrelationId(echoed);
  const contentType = res.headers.get("content-type") ?? "";

  if (!res.ok || contentType.includes("application/problem+json")) {
    // b2c EXPIRED-TOKEN SELF-HEAL: on a 401, the cached access token is stale/revoked. Drop it,
    // silently re-acquire a fresh one, and RETRY the request ONCE with the new token. getAccessToken
    // redirects to B2C login on InteractionRequiredAuthError (the B2C SESSION itself is gone) — the
    // page navigates away and this promise never resolves. So a returning user whose access token
    // merely expired recovers transparently instead of hitting a dead-end 401. `_retried` bounds it
    // to a single attempt (never loops). Server re-resolves the Principal each request, so this is UX
    // recovery, not the security control; the token is never logged.
    if (res.status === 401 && isB2cMode() && !_retried) {
      try {
        const { getAccessToken } = await import("../auth/authService");
        // forceRefresh: bypass MSAL's cache so we mint a genuinely NEW token — clearing an app-level
        // var (the old design) left MSAL's own cache intact, so the retry replayed the same rejected
        // token and forced a spurious sessionExpired. On a gone B2C session getAccessToken redirects.
        await getAccessToken(true);
        // Forward `schema` so the retried response is gated identically; `true` bounds it to one retry.
        // INTENTIONALLY NOT `return await`: the retried call must PROPAGATE its own rejection (an
        // ApiShapeError, or an authRejected=true ApiProblem) straight to the caller. `return await` here
        // would route that rejection into THIS try's `catch`, silently swallowing a terminal auth failure /
        // shape drift and re-surfacing it as a plain first-401. Only the token re-acquire above is guarded;
        // do NOT "fix" this to `return await` (the un-awaited return is correct and covered by tests).
        return fetchEnvelope<T>(path, init, schema, true);
      } catch {
        // Silent re-acquire failed WITHOUT redirecting (e.g. transient network) — fall through and
        // surface the 401 so the session layer can offer a re-auth path.
      }
    }
    let problem: ProblemDetails;
    try {
      problem = (await res.json()) as ProblemDetails;
    } catch {
      // Plain, reassuring fallback title (the numeric status stays on `status` for logs/telemetry, never
      // baked into the user-facing string per the no-jargon copy guard).
      problem = {
        type: "about:blank",
        title: "We could not complete that request. Please try again.",
        status: res.status,
        traceId: "",
      };
    }
    // authRejected: a 401 on the ALREADY-retried request (post-forceRefresh) means a genuinely fresh
    // token was still rejected. Flag it so the session layer maps it to a TERMINAL "cannot verify
    // access" screen instead of "sign in again" (which would mint another rejected token and loop).
    const authRejected = res.status === 401 && _retried;
    throw new ApiProblem(problem, res.status, authRejected);
  }

  const env = (await res.json()) as Envelope<T>;
  if (schema) {
    // SHAPE gate: catch structural drift (array vs non-array, missing required field, wrong primitive
    // type) at the boundary. safeParse never throws; on failure we emit a PHI-SAFE breadcrumb and throw
    // a typed ApiShapeError. On success we return the ORIGINAL env.data (below) — the gate never mutates,
    // strips, or transforms the payload (unknown/extra server keys survive).
    const parsed = schema.safeParse(env.data);
    if (!parsed.success) {
      // SANITIZE: keep ONLY our own schema field-path + the zod error code. NEVER the zod message,
      // expected, received, or input — those can echo PHI values from the payload. Each path SEGMENT is
      // run through sanitizePathSegment so a payload-derived key (e.g. a z.record value failure emits the
      // object's own key) can never be logged verbatim — it collapses to "*".
      const issues: ApiShapeIssue[] = parsed.error.issues.map((i) => ({
        path: joinSanitizedPath(i.path.map(sanitizePathSegment)),
        code: i.code,
      }));
      // L2: CAP the logged issues array — a WIDE-ROW drift (a schema over a fat object/array) can emit
      // ~150 issues, and logging every one bloats a single App Insights event. Keep the TRUE issueCount
      // (so the telemetry still reflects the real magnitude of the drift) but log only the first 20.
      const MAX_LOGGED_ISSUES = 20;
      const loggedIssues = issues.slice(0, MAX_LOGGED_ISSUES);
      // PHI-SAFE telemetry: log a route TEMPLATE (query string stripped — it can carry PII like
      // actorEmail — and id-like segments replaced with ":id"); pass NO Error object (2nd arg undefined),
      // mirroring ErrorBoundary/reportMsalError.
      log.error("API response shape mismatch", undefined, {
        path: templatePath(path),
        issueCount: issues.length,
        issues: loggedIssues,
      });
      // Carry the SAME bounded list on the thrown error (never the full unbounded set).
      throw new ApiShapeError(loggedIssues);
    }
  }
  return { data: env.data, meta: env.meta };
}
